import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Lock, AlertTriangle, Loader2, AlertCircle } from 'lucide-react';
import { tradingApi } from '../../services/tradingApi';
import type { RbacPermissionEntry, RbacRoleWithPermissions } from '../../types/permissions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return all unique permission keys across all roles, preserving category order. */
function getAllPermissions(roles: RbacRoleWithPermissions[]): RbacPermissionEntry[] {
  // Category display order
  const categoryOrder = ['feature', 'admin', 'ea'];

  // Collect all unique permission definitions from any role (they're consistent)
  const byKey = new Map<string, RbacPermissionEntry>();
  for (const role of roles) {
    for (const perm of role.permissions) {
      if (!byKey.has(perm.key)) {
        byKey.set(perm.key, perm);
      }
    }
  }

  // Sort by category then key
  return Array.from(byKey.values()).sort((a, b) => {
    const catA = categoryOrder.indexOf(a.category);
    const catB = categoryOrder.indexOf(b.category);
    if (catA !== catB) return catA - catB;
    return a.key.localeCompare(b.key);
  });
}

/** Get the permission entry for a specific role+key combo. */
function getCell(
  role: RbacRoleWithPermissions,
  permKey: string,
): RbacPermissionEntry | undefined {
  return role.permissions.find((p) => p.key === permKey);
}

// ---------------------------------------------------------------------------
// Category header row
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, string> = {
  feature: 'Feature',
  admin: 'Admin',
  ea: 'EA (Expert Advisor)',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function RbacRoles() {
  const queryClient = useQueryClient();

  // Track which permission row is currently mutating (to disable the row)
  const [mutatingPermission, setMutatingPermission] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const {
    data: roles,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: () => tradingApi.getRbacRoles(),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({
      roleName,
      permKey,
      assign,
    }: {
      roleName: string;
      permKey: string;
      assign: boolean;
    }) => {
      setMutatingPermission(permKey);
      setMutationError(null);
      if (assign) {
        await tradingApi.addRolePermission(roleName, permKey);
      } else {
        await tradingApi.removeRolePermission(roleName, permKey);
      }
    },
    onError: (err: Error) => {
      setMutationError(err.message || 'Failed to update permission');
    },
    onSettled: () => {
      setMutatingPermission(null);
      queryClient.invalidateQueries({ queryKey: ['rbac-roles'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !roles) {
    return (
      <div className="flex items-center gap-3 p-8 text-red-400">
        <AlertCircle className="h-5 w-5" />
        <span>Failed to load role-permission matrix.</span>
      </div>
    );
  }

  const allPermissions = getAllPermissions(roles);
  let lastCategory = '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-900/30 rounded-lg">
          <Shield className="h-5 w-5 text-indigo-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-white">Role Permissions</h1>
          <p className="text-sm text-slate-400">
            Configure which permissions each role has. Changes take effect immediately.
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-slate-500" />
          System-locked — cannot be removed
        </span>
        <span className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
          Seed-managed — removal reverts on backend restart
        </span>
      </div>

      {/* Error banner */}
      {mutationError && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-900/30 border border-red-700/50 rounded-lg text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {mutationError}
        </div>
      )}

      {/* Matrix table */}
      <div className="overflow-x-auto rounded-lg border border-slate-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-800/50">
              <th className="px-4 py-3 text-left text-slate-400 font-medium w-72">Permission</th>
              {roles.map((role) => (
                <th
                  key={role.name}
                  className="px-3 py-3 text-center text-slate-300 font-medium capitalize min-w-[80px]"
                >
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allPermissions.map((perm) => {
              const showCategoryRow = perm.category !== lastCategory;
              lastCategory = perm.category;
              const isRowMutating = mutatingPermission === perm.key;

              return (
                <Fragment key={perm.key}>
                  {showCategoryRow && (
                    <tr className="bg-slate-800/80">
                      <td
                        colSpan={roles.length + 1}
                        className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider"
                      >
                        {CATEGORY_LABELS[perm.category] ?? perm.category}
                      </td>
                    </tr>
                  )}
                  <tr
                    className={`border-t border-slate-700/50 transition-opacity ${
                      isRowMutating ? 'opacity-50 pointer-events-none' : 'hover:bg-slate-800/30'
                    }`}
                  >
                    {/* Permission name + description */}
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs text-slate-300">{perm.key}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{perm.description}</div>
                    </td>

                    {/* One checkbox per role */}
                    {roles.map((role) => {
                      const cell = getCell(role, perm.key);
                      const assigned = cell?.assigned ?? false;
                      const isLocked = cell?.is_locked ?? false;
                      const isSeedManaged = cell?.is_seed_managed ?? false;

                      return (
                        <td key={role.name} className="px-3 py-2.5 text-center">
                          <div className="inline-flex items-center justify-center gap-1 relative group">
                            <input
                              type="checkbox"
                              checked={assigned}
                              disabled={isLocked || isRowMutating}
                              onChange={() => {
                                toggleMutation.mutate({
                                  roleName: role.name,
                                  permKey: perm.key,
                                  assign: !assigned,
                                });
                              }}
                              className={`h-4 w-4 rounded cursor-pointer accent-teal-500 ${
                                isLocked
                                  ? 'cursor-not-allowed opacity-40'
                                  : 'cursor-pointer'
                              }`}
                            />

                            {/* Lock icon for system-locked cells */}
                            {isLocked && (
                              <span
                                className="relative group/lock"
                                title="System-locked — cannot be removed"
                              >
                                <Lock className="h-3 w-3 text-slate-500" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap bg-slate-900 text-slate-300 text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover/lock:opacity-100 pointer-events-none z-10">
                                  System-locked — cannot be removed
                                </span>
                              </span>
                            )}

                            {/* Warning icon for seed-managed, non-locked cells */}
                            {isSeedManaged && !isLocked && assigned && (
                              <span
                                className="relative group/seed"
                                title="Seed-managed"
                              >
                                <AlertTriangle className="h-3 w-3 text-amber-400" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-56 bg-slate-900 text-slate-300 text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover/seed:opacity-100 pointer-events-none z-10 text-center">
                                  Seed-managed: removing this will restore on next backend
                                  restart. Edit <code>rbac_seed.py</code> to make permanent.
                                </span>
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Changes take effect immediately — no backend restart required.
      </p>
    </div>
  );
}
